import { ElLink, ElNotification, type NotificationHandle } from 'element-plus'
import { h, ref, type Ref } from 'vue'

interface WithUndoNotificationProps {
  text: string
  onUndo: () => Promise<void>
}

/**
 * A general purpose notification with 'Undo' action button
 * @param text the text to display
 * @param onUndo the function to run upon clicking 'Undo'
 */
export const withUndoNotification = ({ text, onUndo }: WithUndoNotificationProps) => {
  const notificationHandle = ref<NotificationHandle | null>(null)

  // Show a notification with an undo button
  const message = h('div', { class: 'w-full flex justify-between items-center gap-4' }, [
    h('p', text),
    undoBtn(onUndo, notificationHandle),
  ])

  notificationHandle.value = ElNotification({
    customClass: 'undo-notification',
    position: 'bottom-right',
    message,
    duration: 6 * 1000,
    showClose: false,
  })
}

/**
 * Renders an undo button to restore a deleted spare part.
 * @param effect the undo effect
 * @param notificationHandle a ref to close the notification upon undoing
 */
const undoBtn = (effect: () => Promise<void>, notificationHandle: Ref<NotificationHandle | null>) =>
  h(
    ElLink,
    {
      onClick: async () => {
        await effect()
        notificationHandle.value?.close()
      },
    },
    () => h('span', { style: 'color: var(--text-brand)' }, 'Undo')
  )
